import type { API } from '@/api'
import type { UpdateDeploymentReq } from '@/api/type-request'
import type { ServerDeploymentInfo } from '@/api/type-server'

type DeploymentApi = Pick<typeof API, 'getDeploymentList' | 'updateDeployment'>
type RuntimeEnv = ServerDeploymentInfo['runtimeEnv']
export type DeploymentChanges = Partial<
  Omit<UpdateDeploymentReq, 'res_id' | 'system_env' | 'hardware_resource'>
> & {
  systemEnv?: Partial<RuntimeEnv['systemEnv']>
  hardwareResource?: Partial<RuntimeEnv['hardwareEnv']['resource']>
}

export function buildDeploymentUpdate(
  deployment: ServerDeploymentInfo,
  enabled: boolean,
  changes: DeploymentChanges = {},
): UpdateDeploymentReq {
  const { gateway, runtimeEnv } = deployment
  const systemEnv = runtimeEnv?.systemEnv
  const resource = runtimeEnv?.hardwareEnv?.resource
  if (
    !deployment.resID ||
    typeof deployment.name !== 'string' ||
    typeof deployment.description !== 'string' ||
    !gateway?.resID ||
    !(gateway.methods === null || Array.isArray(gateway.methods)) ||
    gateway.methods?.some((method) => typeof method !== 'string') ||
    !systemEnv ||
    typeof systemEnv.cmd !== 'string' ||
    typeof systemEnv.envs !== 'string' ||
    !Number.isSafeInteger(systemEnv.fromImageId) ||
    !Number.isSafeInteger(systemEnv.fromSnapshotId) ||
    !(systemEnv.extraPorts === null || Array.isArray(systemEnv.extraPorts)) ||
    !resource ||
    !Array.isArray(resource.agentDomains) ||
    resource.agentDomains.length === 0 ||
    !resource.agentDomains.every(Number.isSafeInteger) ||
    ![resource.cpuName, resource.gpuName, resource.domain, resource.machineClassTag].every(
      (value) => typeof value === 'string',
    ) ||
    ![
      resource.cpus,
      resource.gpus,
      resource.diskGB,
      resource.ramMB,
      resource.gpuRamMB,
      resource.agentDriver,
      resource.attributeTags,
      resource.machineCategory,
      resource.unitQty,
    ].every(Number.isFinite)
  ) {
    throw new Error('Incomplete deployment configuration')
  }

  const { systemEnv: systemChanges, hardwareResource: hardwareChanges, ...fields } = changes
  const nextSystemEnv = { ...systemEnv, ...changedValues(systemEnv, systemChanges ?? {}) }
  if (fields.port !== undefined && fields.port !== gateway.port) {
    nextSystemEnv.extraPorts = [...new Set([...(nextSystemEnv.extraPorts ?? []), fields.port])]
  }
  const nextResource = { ...resource, ...changedValues(resource, hardwareChanges ?? {}) }
  const payload: UpdateDeploymentReq = {
    name: deployment.name,
    description: deployment.description,
    gtw_res_id: gateway.resID,
    port: gateway.port,
    methods: gateway.methods ?? [],
    enabled,
    ttl_seconds: deployment.ttlSeconds,
    max_replicas: deployment.maxReplicas,
    scaling_mode: deployment.scalingMode ?? 0,
    concurrency_per_replica: deployment.concurrencyPerReplica ?? 0,
    max_pending_requests: deployment.maxPendingRequests ?? 0,
    domain: nextResource.agentDomains[0],
    ...fields,
    res_id: deployment.resID,
    system_env: JSON.stringify(nextSystemEnv),
    hardware_resource: JSON.stringify(nextResource),
  }
  if (
    ![0, 1].includes(payload.scaling_mode) ||
    ![
      payload.port,
      payload.ttl_seconds,
      payload.concurrency_per_replica,
      payload.max_pending_requests,
    ].every((value) => Number.isSafeInteger(value) && value >= 0) ||
    !Number.isSafeInteger(payload.max_replicas) ||
    payload.max_replicas < 1 ||
    (payload.scaling_mode === 1 && payload.concurrency_per_replica === 0) ||
    typeof payload.enabled !== 'boolean'
  ) {
    throw new Error('Invalid deployment configuration')
  }
  return payload
}

function changedValues<T extends object>(current: T, changes: Partial<T>): Partial<T> {
  return Object.fromEntries(
    Object.entries(changes).filter(
      ([key, value]) => value !== undefined && !Object.is(current[key as keyof T], value),
    ),
  ) as Partial<T>
}

// Only apply fields edited in this form; preserve newer server values for untouched fields.
export function getDeploymentEditChanges(
  original: ServerDeploymentInfo,
  changes: DeploymentChanges,
): DeploymentChanges {
  const { systemEnv, hardwareResource, ...fields } = changes
  const resource = original.runtimeEnv.hardwareEnv.resource
  const machineChanged =
    hardwareResource &&
    (hardwareResource.machineClassTag !== resource.machineClassTag ||
      hardwareResource.machineCategory !== resource.machineCategory ||
      !resource.agentDomains.includes(hardwareResource.agentDomains?.[0] ?? -1))
  const edited = changedValues(buildDeploymentUpdate(original, false), fields)
  if (!machineChanged) {
    delete edited.gtw_res_id
    delete edited.domain
  }
  return {
    ...edited,
    systemEnv: changedValues(original.runtimeEnv.systemEnv, systemEnv ?? {}),
    ...(machineChanged ? { hardwareResource } : {}),
  }
}

export async function updateDeploymentPreservingConfig(
  api: DeploymentApi,
  resID: string,
  changes: DeploymentChanges,
) {
  // enabled is absent from each item. Query both lists to retain the current state.
  const matches = await Promise.all(
    [true, false].map(async (enabled) => {
      let page = 1
      let pageCount = 1
      do {
        const response = await api.getDeploymentList({ enabled, page, per_page: 100 })
        if (response.code !== 0 || !Array.isArray(response.deployments)) {
          throw new Error('Failed to read deployment configuration')
        }
        const deployment = response.deployments.find((item) => item.resID === resID)
        if (deployment) return { deployment, enabled }
        // total includes both enabled states; a short filtered page is not the end.
        const { total, perPage } = response.pagination ?? {}
        if (
          !Number.isSafeInteger(total) ||
          total < 0 ||
          !Number.isSafeInteger(perPage) ||
          perPage < 1
        ) {
          throw new Error('Incomplete deployment pagination')
        }
        pageCount = Math.ceil(total / perPage)
        page += 1
      } while (page <= pageCount)
      return undefined
    }),
  )
  const found = matches.filter((match) => match !== undefined)
  if (found.length !== 1) throw new Error('Deployment is missing or its state changed; retry')
  const { deployment, enabled } = found[0]!
  // shortcut: read-before-write is not atomic; server version checks are needed for concurrent writes.
  const response = await api.updateDeployment(buildDeploymentUpdate(deployment, enabled, changes))
  if (response.code !== 0) throw new Error('Failed to update deployment')
  return response
}
